import iconDark from '@/assets/brand/onestem-icon-dark.svg'
import iconLight from '@/assets/brand/onestem-icon.svg'
import wordmarkDark from '@/assets/brand/onestem-wordmark-dark.svg'
import wordmarkLight from '@/assets/brand/onestem-wordmark.svg'
import { ADMIN_PRODUCT_NAME } from '@/lib/brand'
import { cn } from '@/lib/utils'
import type { ComponentProps, HTMLAttributes } from 'react'

const sources = {
  icon: { light: iconLight, dark: iconDark },
  wordmark: { light: wordmarkLight, dark: wordmarkDark },
}

type OneStemBrandProps = HTMLAttributes<HTMLElement> & {
  variant?: keyof typeof sources
  surface?: 'auto' | 'light' | 'dark'
  decorative?: boolean
}

function BrandImage({ alt, className, src, ...props }: ComponentProps<'img'>) {
  return <img src={src} alt={alt} className={cn('block max-w-full', className)} {...props} />
}

export function OneStemBrand({
  variant = 'wordmark',
  surface = 'auto',
  className,
  decorative = false,
  ...props
}: OneStemBrandProps) {
  const variantSources = sources[variant]

  if (!variantSources) {
    throw new Error(`Unsupported OneStem brand variant: ${variant}`)
  }

  const alt = decorative ? '' : ADMIN_PRODUCT_NAME

  if (surface !== 'auto') {
    return <BrandImage src={variantSources[surface]} alt={alt} className={className} {...props} />
  }

  return (
    <span
      role={decorative ? undefined : 'img'}
      aria-label={decorative ? undefined : ADMIN_PRODUCT_NAME}
      className={cn('relative inline-block', className)}
      {...props}
    >
      <BrandImage
        src={variantSources.light}
        alt=""
        aria-hidden="true"
        className="h-full w-auto object-contain dark:invisible"
      />
      <BrandImage
        src={variantSources.dark}
        alt=""
        aria-hidden="true"
        className="absolute inset-0 h-full w-auto object-contain opacity-0 dark:opacity-100"
      />
    </span>
  )
}
